import { DownloadOutlined } from '@ant-design/icons';
import { debounce } from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { message, notification, Space } from 'antd';

import { useAppDispatch } from '../../../../app/hooks';
import {
  Alert,
  Button,
  DatePicker,
  SearchInput,
} from '../../../../components/index';
import { useChangePagination } from '../../../../hooks';
import { useMutationWithErrorControl } from '../../../../hooks/useMutationWithErrorControl';
import { useExportXmlMutation } from '../../../../services/dockingApi';
import { useLazyExportReportsQuery } from '../../../../services/exportApi';
import { useGetStopoveresGroupbyDockingStatisticsQuery } from '../../../../services/stopoverApi';
import { tosApi } from '../../../../services/tosApi';
import {
  DockingBillingStatusType,
  StopoverGroupbyDockingStatisticsType,
} from '../../../../types';
import { createDateString, downloadFile } from '../../../../utils/utils';
import { SectionHeader } from '../styles';
import { DockingObservationsModal } from './DockingObservationsModal';
import { DockingReportDrawer } from './dockingReportDrawer/DockingReportDrawer';
import { hasAntaqCode } from './dockingsTable/columns';
import { DockingsTable } from './dockingsTable/DockingsTable';

export type DockingsFiltersType = {
  initial_date_report?: string | null;
  final_date_report?: string | null;
  code_or_vessel__name?: string;
  page?: number;
};

const BUSINESS_RULE_STATUS = 409;
const WASTE_COLLECTION_REFETCH_DELAYS = [5000, 15000, 30000, 60000];

function getExportXmlErrorDescription(error: unknown): string {
  const { status, data } = (error || {}) as {
    status?: number | string;
    data?: { message?: string };
  };

  if (status === BUSINESS_RULE_STATUS && data?.message) {
    return `${data.message}. O preenchimento é feito na aba "Meio ambiente".`;
  }

  return data?.message || 'Erro ao gerar XML. Tente novamente.';
}

export function DockingsSection() {
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();

  const [dockingFilters, setDockingFilters] = useState<DockingsFiltersType>({
    page: queryPage,
  } as DockingsFiltersType);

  // Fora de dockingFilters porque onSearch e onChangeDatePicker reescrevem
  // aquele objeto inteiro, o que apagaria a seleção sem o cabeçalho saber.
  const [selectedBillingStatus, setSelectedBillingStatus] = useState<
    DockingBillingStatusType[]
  >([]);

  function isAllDockingSelected() {
    if (!dockingsData || !dockingsData.results) {
      return false;
    }

    // Linha sem código ANTAQ tem o checkbox desabilitado, então ela não conta
    // para "todas selecionadas": incluí-la deixaria o cabeçalho sempre desmarcado.
    const selectableDockings = dockingsData.results.filter((item) =>
      hasAntaqCode(item)
    );

    return (
      selectableDockings.length > 0 &&
      selectableDockings.every((item) =>
        dockingsIdToEdit.includes(item.docking__id)
      )
    );
  }

  useEffect(() => {
    setDockingFilters((prev) => ({ ...prev, page: queryPage }));
  }, [queryPage]);

  const [dockingsIdToEdit, setDockingsIdToEdit] = useState<number[]>([]);

  const [isVisibleDockingReportDrawer, setIsVisibleDockingReportDrawer] =
    useState(false);

  const [isVisibleObservationsModal, setIsVisibleObservationsModal] =
    useState(false);

  const [dockingToObserve, setDockingToObserve] =
    useState<StopoverGroupbyDockingStatisticsType>();

  const { data: dockingsData, isFetching: isFetchingDockingsData } =
    useGetStopoveresGroupbyDockingStatisticsQuery({
      ...dockingFilters,
      billing_status__in:
        selectedBillingStatus.length > 0
          ? selectedBillingStatus.join(',')
          : undefined,
    });

  // A troca de seleção volta para a primeira página: o conjunto filtrado é
  // outro e a página corrente pode não existir mais nele.
  function onChangeBillingStatus(status: DockingBillingStatusType[]) {
    setSelectedBillingStatus(status);
    setQueryPage(1);
    setDockingFilters((prev) => ({ ...prev, page: 1 }));
  }

  useEffect(() => {
    if (!isFetchingDockingsData) {
      if (isAllDockingSelected()) {
        setCheckedAll(true);
      } else {
        setCheckedAll(false);
      }
    }
  }, [dockingsData]);

  const [exportXml, { isLoading: isLoadingExportXml }] = useExportXmlMutation();
  const exportXmlWithErrorControl = useMutationWithErrorControl(exportXml);

  const dispatch = useAppDispatch();
  const wasteCollectionRefetchTimersRef = useRef<
    ReturnType<typeof setTimeout>[]
  >([]);

  useEffect(() => {
    return () => wasteCollectionRefetchTimersRef.current.forEach(clearTimeout);
  }, []);

  function scheduleWasteCollectionRefetch() {
    wasteCollectionRefetchTimersRef.current.forEach(clearTimeout);
    wasteCollectionRefetchTimersRef.current =
      WASTE_COLLECTION_REFETCH_DELAYS.map((delay) =>
        setTimeout(
          () =>
            dispatch(
              tosApi.util.invalidateTags([
                { type: 'WasteCollection', id: 'LIST' },
              ])
            ),
          delay
        )
      );
  }
  const [exportDockingsReport, { isLoading: isExporting }] =
    useLazyExportReportsQuery();

  const [selectedDocking, setSelectedDocking] = useState(
    {} as StopoverGroupbyDockingStatisticsType
  );

  const [checkedAll, setCheckedAll] = useState(false);

  function onSelectViewDocking(docking: StopoverGroupbyDockingStatisticsType) {
    setSelectedDocking(docking);
    setIsVisibleDockingReportDrawer(true);
  }

  function onOpenObservations(docking: StopoverGroupbyDockingStatisticsType) {
    setDockingToObserve(docking);
    setIsVisibleObservationsModal(true);
  }

  function onSearch(value: string) {
    if (value !== '') {
      setDockingFilters({ code_or_vessel__name: value });
    } else {
      setDockingFilters((prev) => ({ ...prev, code_or_vessel__name: '' }));
    }
  }

  function onChangeDatePicker(dates: any) {
    if (dates === null) {
      setDockingFilters((prev) => ({
        code_or_vessel__name: prev.code_or_vessel__name,
      }));
      return;
    }
    let filteredDates = {};
    if (dates && dates.length > 0 && dates[0]) {
      filteredDates = {
        ...filteredDates,
        initial_date_report: createDateString(dates[0], true),
      };
    }
    if (dates && dates.length > 1 && dates[1]) {
      filteredDates = {
        ...filteredDates,
        final_date_report: createDateString(dates[1], true),
      };
    }
    setDockingFilters((prev) => ({ ...prev, ...filteredDates }));
  }

  function handleCheckDocking(isChecked: boolean, dockingId: number) {
    if (isChecked) {
      setDockingsIdToEdit((prev) => [...prev, dockingId]);
    } else {
      setDockingsIdToEdit((prev) => [
        ...prev.filter((item) => item !== dockingId),
      ]);
      setCheckedAll(false);
    }
  }

  function handleCheckedAll(checked: boolean) {
    if (checked) {
      setCheckedAll(true);
      setDockingsIdToEdit((prev) => [
        ...prev,
        ...(dockingsData?.results
          .filter((item) => !prev.includes(item.docking__id))
          .filter((item) => hasAntaqCode(item))
          .flatMap((item) => item.docking__id) || []),
      ]);
    } else {
      setCheckedAll(false);
      setDockingsIdToEdit(
        dockingsIdToEdit.filter((id) => {
          return !dockingsData?.results
            .flatMap((item) => item.docking__id)
            .includes(id);
        })
      );
    }
  }

  async function handleGenerateXml() {
    let filters = {};
    if (dockingsIdToEdit.length > 0) {
      filters = {
        list_of_docking_ids: dockingsIdToEdit,
      };
    } else if (
      dockingFilters.initial_date_report &&
      dockingFilters.final_date_report
    ) {
      filters = {
        initial_date_report: dockingFilters.initial_date_report,
        final_date_report: dockingFilters.final_date_report,
      };
    }
    try {
      await exportXmlWithErrorControl(filters, {
        suppressErrorAlert: true,
      }).unwrap();
      scheduleWasteCollectionRefetch();
      message.success(
        'XML em processamento. Você o receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      notification.error({
        message: 'Não foi possível gerar o XML',
        description: getExportXmlErrorDescription(error),
        duration: 10,
      });
    }
  }

  async function handleExport() {
    try {
      await exportDockingsReport({
        exportType: 'dockings',
        params: {
          search: dockingFilters.code_or_vessel__name,
          initial_date: dockingFilters.initial_date_report || undefined,
          final_date: dockingFilters.final_date_report || undefined,
        },
      }).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  return (
    <>
      <SectionHeader style={{ flexDirection: 'column', gap: '15px' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
              marginInlineEnd: '10px',
            }}
          >
            <SearchInput
              size="large"
              allowClear
              placeholder="Pesquisar embarcação/atracação"
              style={{ width: '400px' }}
              onChange={debounce((evt) => onSearch(evt.target.value), 500)}
            />
            <div>
              <strong>{dockingsIdToEdit.length} </strong>
              {dockingsIdToEdit.length === 1 ? (
                <span>atracação selecionada</span>
              ) : (
                <span>atracações selecionadas</span>
              )}
            </div>
            <Alert
              type="warning"
              message="Selecione as atracações desejadas para ativar a geração do XML.
              É possível selecionar um intervalo de atracações ao filtrar por data."
            />
          </div>
          <Space size={18}>
            <Button
              icon={<DownloadOutlined />}
              size="large"
              onClick={handleExport}
              loading={isExporting}
            >
              Exportar XLS
            </Button>
            <Button
              type="primary"
              onClick={() => handleGenerateXml()}
              disabled={dockingsIdToEdit.length === 0}
              loading={isLoadingExportXml}
            >
              Gerar XML
            </Button>
          </Space>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginRight: 'auto',
            marginBottom: '20px',
          }}
        >
          <span style={{ width: '240px' }}>
            Filtro por período de desatracação:{'   '}
          </span>
          <DatePicker
            allowClear
            onChange={onChangeDatePicker}
            minuteStep={1}
            isRangePicker
            showTime={false}
            format={['DD/MM/YYYY']}
            style={{ width: '240px' }}
          />
        </div>
      </SectionHeader>
      <DockingsTable
        rowKey="docking__id"
        dataSource={dockingsData?.results || []}
        onChangePagination={onChangePagination}
        total={dockingsData?.count}
        isLoading={isFetchingDockingsData}
        queryPage={queryPage}
        showSizeChanger={false}
        onOpenDrawer={onSelectViewDocking}
        onSelectItem={handleCheckDocking}
        dockingsIdToEdit={dockingsIdToEdit}
        checkedAll={checkedAll}
        onCheckedAll={handleCheckedAll}
        itemsPerPage={dockingsData?.results.length}
        selectedBillingStatus={selectedBillingStatus}
        onChangeBillingStatus={onChangeBillingStatus}
        onOpenObservations={onOpenObservations}
      />
      <DockingObservationsModal
        isVisible={isVisibleObservationsModal}
        onClose={() => setIsVisibleObservationsModal(false)}
        docking={dockingToObserve}
      />
      <DockingReportDrawer
        isVisible={isVisibleDockingReportDrawer}
        setIsVisible={setIsVisibleDockingReportDrawer}
        selectedDocking={selectedDocking}
        setSelectedDocking={setSelectedDocking}
      />
    </>
  );
}
